import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { LeaderLifecycleState } from "@/hooks/useLeaderLifecycle";
import { PREVIEW_LEADER_USER_ID } from "@/preview/leaderPreviewContext";

export interface MyLifecycleData {
  lifecycle_state: LeaderLifecycleState;
  closing_note: string | null;
  access_ends_at: string | null;
  /** Effective state accounting for expired access */
  effective_state: LeaderLifecycleState;
  /** True if access has expired (access_ends_at is in the past) */
  access_expired: boolean;
}

export function useMyLifecycleState() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["my-lifecycle-state", user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      if (user.id === PREVIEW_LEADER_USER_ID) {
        return {
          lifecycle_state: "active",
          closing_note: null,
          access_ends_at: null,
          effective_state: "active",
          access_expired: false,
        } as MyLifecycleData;
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("lifecycle_state, closing_note, access_ends_at")
        .eq("id", user.id)
        .single();

      if (error) throw error;
      
      const lifecycleState = (data?.lifecycle_state as LeaderLifecycleState) || "active";
      const accessEndsAt = data?.access_ends_at as string | null;
      
      // Check if access has expired (for completed leaders whose 30-day window passed)
      const accessExpired = accessEndsAt ? new Date(accessEndsAt) <= new Date() : false;
      
      // Effective state: treat expired completed leaders as archived for access control
      let effectiveState = lifecycleState;
      if (lifecycleState === "completed" && accessExpired) {
        effectiveState = "archived";
        // Log warning for debugging - cron may not be running
        console.warn(
          "[Lifecycle] Leader access has expired but state is still 'completed'. " +
          "The auto-archive cron job may not be configured. " +
          `access_ends_at: ${accessEndsAt}`
        );
      }
      
      return {
        lifecycle_state: lifecycleState,
        closing_note: data?.closing_note as string | null,
        access_ends_at: accessEndsAt,
        effective_state: effectiveState,
        access_expired: accessExpired,
      } as MyLifecycleData;
    },
    enabled: !!user?.id,
  });
}

export function useIsReadOnlyMode() {
  const { data } = useMyLifecycleState();
  
  // Read-only when completed, archived, or access has expired
  if (!data) return false;
  return (
    data.effective_state === "completed" || 
    data.effective_state === "archived" ||
    data.access_expired
  );
}

/** Check if leader access has fully ended (archived or expired) */
export function useIsAccessEnded() {
  const { data } = useMyLifecycleState();
  
  if (!data) return false;
  return data.effective_state === "archived" || data.access_expired;
}
